import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Trophy, Users, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import CalendarSubscribe from "../components/CalendarSubscribe";
import { useGames } from "../context/GamesContext";

const MULTI_LABEL = "Multi-Title";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function Tournaments() {
  const { labelOf } = useGames();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | upcoming | live | completed

  useEffect(() => {
    api
      .get("/tournaments")
      .then((r) => setAll(r.data))
      .catch(() => setAll([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const sorted = [...all].sort((a, b) => new Date(b.date) - new Date(a.date));
    if (filter === "all") return sorted;
    return sorted.filter((t) => t.status === filter);
  }, [all, filter]);

  const counts = useMemo(() => {
    const c = { all: all.length, upcoming: 0, live: 0, completed: 0 };
    for (const t of all) if (c[t.status] != null) c[t.status]++;
    return c;
  }, [all]);

  const filters = [
    { key: "all", label: "All" },
    { key: "upcoming", label: "Upcoming" },
    { key: "live", label: "Live" },
    { key: "completed", label: "Completed" },
  ];

  return (
    <div data-testid="tournaments-page" className="text-white">
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.35em] text-axe-tangerine font-bold">
              Schedule
            </div>
            <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl">
              Tournaments & <span className="text-axe-purple">Events</span>
            </h1>
            <p className="mt-5 max-w-2xl text-neutral-400 text-base sm:text-lg">
              Circuits we're competing in, community events we're hosting, and the results we've
              earned along the way.
            </p>
          </div>
          <CalendarSubscribe testId="tournaments-subscribe" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-10">
        <div className="flex flex-wrap gap-2">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`tournaments-filter-${f.key}`}
              className={`px-5 py-2 rounded-full text-[11px] uppercase tracking-[0.25em] font-bold transition-colors border ${
                filter === f.key
                  ? "bg-axe-purple border-axe-purple text-white drop-shadow-[0_0_14px_rgba(176,38,255,0.45)]"
                  : "bg-axe-surface border-axe-border text-neutral-400 hover:text-white"
              }`}
            >
              {f.label} · {counts[f.key]}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-10">
        {loading ? (
          <div className="text-neutral-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-neutral-500 border border-axe-border rounded-2xl p-8 text-center" data-testid="tournaments-empty">
            Nothing in this bucket yet.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {filtered.map((t) => (
              <Link
                key={t.id}
                to={`/tournaments/${t.id}`}
                data-testid={`tournament-card-${t.id}`}
                className="group relative overflow-hidden rounded-2xl border border-axe-border bg-axe-surface hover:border-axe-purple/60 transition-all block"
              >
                {t.image && (
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img src={t.image} alt={t.name} className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-axe-surface via-axe-surface/30 to-transparent" />
                    <div className="absolute top-4 left-4 flex gap-2">
                      <span
                        className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.25em] font-bold ${
                          t.status === "live"
                            ? "bg-red-500 text-white animate-pulse"
                            : t.status === "upcoming"
                            ? "bg-axe-tangerine text-black"
                            : "bg-axe-purple/80 text-white"
                        }`}
                      >
                        {t.status}
                      </span>
                      <span className="px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.25em] font-bold bg-black/60 text-white border border-white/10">
                        {t.game === "multi" ? MULTI_LABEL : labelOf(t.game)}
                      </span>
                    </div>
                  </div>
                )}
                <div className="p-6">
                  <h3 className="font-display font-bold text-xl text-white leading-tight">
                    {t.name}
                  </h3>
                  <div className="mt-4 flex flex-wrap gap-4 text-sm text-neutral-400">
                    <span className="inline-flex items-center gap-2">
                      <Calendar size={14} className="text-axe-tangerine" /> {fmtDate(t.date)}
                    </span>
                    {t.prize_pool && (
                      <span className="inline-flex items-center gap-2">
                        <Trophy size={14} className="text-axe-tangerine" /> {t.prize_pool}
                      </span>
                    )}
                    {t.result && (
                      <span className="inline-flex items-center gap-2">
                        <Users size={14} className="text-axe-tangerine" /> {t.result}
                      </span>
                    )}
                  </div>
                  {t.registration_url && t.registration_url !== "#" && t.status !== "completed" && (
                    <a
                      href={t.registration_url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="mt-5 inline-flex items-center gap-2 rounded-full bg-axe-purple px-5 py-2 text-[11px] uppercase tracking-[0.25em] font-bold text-white hover:bg-axe-purple-hover"
                      data-testid={`tournament-register-${t.id}`}
                    >
                      Register <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
